import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { AttachmentType } from '@interfaces/common/attachmentType';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { AttachmentTypeService } from '@services/http/attachment-type.service';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable } from 'rxjs';

@Component({
  templateUrl: './attachment-type-list.component.html',
  styleUrls: ['./attachment-type-list.component.scss'],
  standalone: false,
})
export class AttachmentTypeListComponent extends VaultBaseComponent implements OnInit {
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  attachmentTypes: AttachmentType[] = [];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 5,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  attachmentTypePermissions = Permissions.DerivativeAttachmentType;
  isMobile: boolean = false;
  /** Listagem em andamento: começa verdadeiro para a tabela abrir no esqueleto, não em "Nenhum item". */
  loading = true;
  /** A primeira listagem já chegou: daí em diante o recarregamento usa o loading global. */
  private attachmentTypesLoadedOnce = false;

  constructor(
    private toast: ToastHelperService,
    private router: Router,
    private utilsService: UtilsService,
    private attachmentTypeService: AttachmentTypeService,
    translateService: TranslateService,
    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/',
      },
      {
        label: this.t(this.tk.Management.AttachmentTypes),
        routerLink: '/dashboard/attachment-type/list',
      },
    ];

    this.getAttachmentTypes();
  }

  getAttachmentTypes() {
    // Primeira carga: a tabela mostra o esqueleto, sem o loading global por cima.
    // Recarregamentos (busca, página, exclusão) seguem com o loading global.
    const showGlobalLoading = this.attachmentTypesLoadedOnce;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);
    this.loading = true;
    this.attachmentTypeService
      .getAttachmentTypesList(
        {
          ...this.paginationRequest,
          Page: (this.paginationRequest?.Page || 0) + 1,
        },
        this.search
      )
      .subscribe({
        next: response => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.attachmentTypesLoadedOnce = true;
          this.loading = false;
          if (response) {
            this.attachmentTypes = response.data;
            const { total, currentPage, pages, itemsPerPage } = response;

            this.pagination = {
              total: total as number,
              currentPage: currentPage as number,
              pages,
              itemsPerPage: itemsPerPage as number,
            };
          }
        },
        error: error => {
          if (showGlobalLoading) this.utilsService.setIsLoading(false);
          this.attachmentTypesLoadedOnce = true;
          this.loading = false;
          console.error('Error: ', error);

          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingAttachmentTypes),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  handleTablePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getAttachmentTypes();
  }

  addAttachmentType(): void {
    this.router.navigate(['dashboard/attachment-type/register']);
  }

  handleTableAction(tableActions: TableActions<AttachmentType>): void {
    switch (tableActions.action) {
      case 'delete':
        tableActions.items.forEach((user: AttachmentType, iUser: number) => {
          this.utilsService.setIsLoading(true);
          this.removeAttachmentType(user).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(
                this.t(this.tk.Management.AttachmentTypeRemovedSuccessfully),
                ''
              );
              if (iUser === tableActions.items.length - 1) {
                this.attachmentTypes = [];
                this.getAttachmentTypes();
              }
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              console.log('removeUser error: ', error);

              this.toast.showToastError(
                this.t(this.tk.Management.ErrorRemovingAttachmentType),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });

        break;
      default:
        break;
    }
  }

  removeAttachmentType(attachmentType: AttachmentType): Observable<AttachmentType | null> {
    return this.attachmentTypeService.deleteAttachmentType(attachmentType.id as string);
  }

  handleSearch(search: string = ''): void {
    this.search = search;
    this.getAttachmentTypes();
  }

  verifyIsMobile() {
    if (this.store) {
      this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
    }
  }
}
